import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import clsx from "clsx";
import "./IconTooltip.scss";

export const ICON_TOOLTIP_HOST = "has-tooltip-portal";

const GAP = 4;
const HIDE_DELAY = 100;
const EDGE_OFFSET = 10;

const canHover = () => typeof window.matchMedia === "function" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const isKeyboardFocus = (el) => {
    if (document.documentElement.hasAttribute("data-pointer-focus")) return false;
    try {
        return el.matches(":focus-visible");
    } catch (_) {
        return true;
    }
};

const IconTooltip = ({ label, hideWhenExpanded = true }) => {
    const anchorRef = useRef(null);
    const tipRef = useRef(null);
    const hideTimerRef = useRef(null);
    const hoveredRef = useRef(false);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);

    const getHost = () => anchorRef.current?.parentElement || null;

    const show = useCallback(() => {
        clearTimeout(hideTimerRef.current);
        if (hideWhenExpanded && getHost()?.getAttribute("aria-expanded") === "true") return;
        setOpen(true);
    }, [hideWhenExpanded]);

    const hide = useCallback(() => {
        clearTimeout(hideTimerRef.current);
        setOpen(false);
        setPos(null);
    }, []);

    const hideSoon = useCallback(() => {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(hide, HIDE_DELAY);
    }, [hide]);

    useEffect(() => {
        const host = getHost();
        if (!host) return undefined;
        const onEnter = () => {
            hoveredRef.current = true;
            if (canHover()) show();
        };
        const onLeave = () => {
            hoveredRef.current = false;
            hideSoon();
        };
        const onFocus = () => { if (isKeyboardFocus(host)) show(); };
        const onBlur = () => { if (!hoveredRef.current) hide(); };
        const onPointerDown = () => { if (!canHover()) hide(); };
        const expandObserver = new MutationObserver(() => {
            if (hideWhenExpanded && host.getAttribute("aria-expanded") === "true") hide();
        });
        host.addEventListener("mouseenter", onEnter);
        host.addEventListener("mouseleave", onLeave);
        host.addEventListener("focus", onFocus);
        host.addEventListener("blur", onBlur);
        host.addEventListener("pointerdown", onPointerDown);
        expandObserver.observe(host, { attributes: true, attributeFilter: ["aria-expanded"] });
        return () => {
            host.removeEventListener("mouseenter", onEnter);
            host.removeEventListener("mouseleave", onLeave);
            host.removeEventListener("focus", onFocus);
            host.removeEventListener("blur", onBlur);
            host.removeEventListener("pointerdown", onPointerDown);
            expandObserver.disconnect();
            clearTimeout(hideTimerRef.current);
        };
    }, [show, hide, hideSoon, hideWhenExpanded]);

    const place = useCallback(() => {
        const host = getHost();
        const tip = tipRef.current;
        if (!host || !tip) return;
        const r = host.getBoundingClientRect();
        if (!r.width && !r.height) {
            hide();
            return;
        }
        const w = tip.offsetWidth;
        const h = tip.offsetHeight;
        const above = r.bottom + h > window.innerHeight && r.top - h >= 0;
        const left = Math.min(Math.max(GAP, r.right + EDGE_OFFSET - w), window.innerWidth - w - GAP);
        const arrowRight = Math.min(Math.max(4, left + w - (r.left + r.width / 2) - 10), w - 24);
        const top = above ? r.top - h : r.bottom;
        setPos((prev) => (prev && prev.top === top && prev.left === left && prev.above === above && prev.arrowRight === arrowRight
            ? prev
            : { top, left, above, arrowRight }));
    }, [hide]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        place();
        let frame = requestAnimationFrame(function follow() {
            place();
            frame = requestAnimationFrame(follow);
        });
        const onKeyDown = (e) => { if (e.key === "Escape") hide(); };
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, [open, place, hide]);

    return (
        <>
            <span ref={anchorRef} hidden />
            {open && createPortal(
                <div
                    ref={tipRef}
                    className={clsx("custom-tooltip tooltip-auto-width tooltip-portal", pos?.above && "top-tooltip")}
                    style={pos ? { top: pos.top, left: pos.left, "--tooltip-portal-arrow-right": `${pos.arrowRight}px` } : { visibility: "hidden", top: 0, left: 0 }}
                    aria-hidden="true"
                    data-testid="iconTooltip"
                    onMouseEnter={show}
                    onMouseLeave={hideSoon}
                >
                    <div className="custom-tooltip-content text-nowrap">
                        <div className="custom-tooltip-in">
                            <p>{label}</p>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

IconTooltip.propTypes = {
    label: PropTypes.string,
    hideWhenExpanded: PropTypes.bool,
};

export default IconTooltip;
